import { css } from '@emotion/react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  COLOR_PALETTE,
  Ellipsis,
  Flex,
  Group,
  Stack,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { Currency } from '@inflearn/i18n-tools';
import { GiftStatus } from '../../../../../pages/my/gifts/sent';
import useMediaQuery from '../../../../../styles/useMediaQuery';
import { GiftInfoRow } from '../common/GiftInfoRow';
import { GiftItemHeader } from '../common/GiftItemHeader';

export interface SentGiftItemProps {
  id: number;
  orderDate: string;
  courseTitle: string;
  recipientName: string;
  refundDeadline: string;
  amount: number;
  status: GiftStatus;
  receiptUrl: string;
  detailURL: string;
  courseDetailURL: string;
}

const StatusBadge = ({ status }: { status: SentGiftItemProps['status'] }) => {
  switch (status) {
    case GiftStatus.PENDING:
      return (
        <Badge variant="light" color="orange" size="md" radius="sm" w="fit-content">
          수락 전
        </Badge>
      );
    case GiftStatus.ACCEPTED:
      return (
        <Badge variant="light" color="blue" size="md" radius="sm" w="fit-content">
          수락 완료
        </Badge>
      );
    case GiftStatus.REFUNDED:
      return (
        <Badge variant="filled" color="gray" size="md" radius="sm" w="fit-content">
          환불
        </Badge>
      );
    default:
      return null;
  }
};

export const SentGiftItem = ({ item }: { item: SentGiftItemProps }) => {
  const { isMobile } = useMediaQuery();

  return (
    <Stack spacing={8}>
      <GiftItemHeader dateLabel="주문 날짜" date={item.orderDate} href={item.detailURL} />

      <Box
        p={16}
        css={{
          borderRadius: '8px',
          border: `1px solid ${COLOR_PALETTE.gray[2]}`,
        }}
      >
        <Stack spacing={8}>
          <StatusBadge status={item.status} />

          <Group spacing={isMobile ? 12 : 48} align="flex-start" css={styleProperties}>
            <Stack spacing={4} className="properties" css={{ flexGrow: 1 }}>
              <GiftInfoRow
                label="선물한 강의"
                value={
                  <Ellipsis lineClamp={1}>
                    <Anchor
                      href={item.courseDetailURL}
                      size="sm"
                      color="dark"
                      weight={600}
                      target="_blank"
                    >
                      {item.courseTitle}
                    </Anchor>
                  </Ellipsis>
                }
              />
              <GiftInfoRow label="선물 받는 분" value={item.recipientName} />
              {item.refundDeadline && <GiftInfoRow label="환불 기한" value={item.refundDeadline} />}
            </Stack>

            <Flex justify="space-between" css={stylePrice} className="price">
              <Text size="xs" weight={700} miw={40}>
                금액
              </Text>
              <Text size="xs" weight={700}>
                <Currency amount={item.amount} currency="KRW" />
              </Text>
            </Flex>

            <Stack
              spacing={isMobile ? 8 : 4}
              align="flex-end"
              w={95}
              css={{ grow: 1 }}
              className="buttons"
            >
              {item.receiptUrl && (
                <Button
                  component="a"
                  href={item.receiptUrl}
                  target="_blank"
                  variant="default"
                  color="infgreen"
                  size={isMobile ? 'sm' : 'xs'}
                  radius="md"
                  w="100%"
                >
                  영수증
                </Button>
              )}
            </Stack>
          </Group>
        </Stack>
      </Box>
    </Stack>
  );
};

const styleProperties = css({
  justifyContent: 'space-between',
  flexGrow: 1,
  [desktopFirstMediaQuery.mobile]: {
    flexShrink: 0,
    flexWrap: 'wrap',
    '.properties': {
      flexGrow: 1,
    },
    '.price': {
      width: '100%',
      flexGrow: 1,
    },
    '.buttons': {
      flexGrow: 1,
      flexDirection: 'row',
    },
  },
});

const stylePrice = css({
  flexGrow: 0,
  [desktopFirstMediaQuery.mobile]: {
    paddingTop: 12,
    paddingBottom: 12,
    borderTop: `1px solid ${COLOR_PALETTE.gray[2]}`,
  },
});
